import React from "react";
import clsx from "clsx";

import Select from 'react-select';
import { makeStyles, useTheme } from "@mui/styles";
import {emphasize } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import NoSsr from "@mui/material/NoSsr";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import CancelIcon from "@mui/icons-material/Cancel";
import PropTypes from "prop-types";
import { debounce } from 'lodash';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
    // height: 250,
  },
  input: {
    display: "flex",
    padding: 0,
    height: "auto",
    color: props => props.disabled && "rgba(0,0,0,.3)"
  },
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden"
  },
  chip: {
    margin: theme.spacing(0.5, 0.25)
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.mode === "light"
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08
    )
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2)
  },
  singleValue: {
    fontSize: 16
  },
  placeholder: {
    position: "absolute",
    left: 2,
    bottom: 6,
    fontSize: 16
  },
  paper: {
    position: "absolute",
    zIndex: 10,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing(2)
  }
}));

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

NoOptionsMessage.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired
};

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

inputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};

function Control(props) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps }
  } = props;
  return (
    <TextField
      variant="standard"
      error={!!props.hasError}
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps
        }
      }}
      {...TextFieldProps} />
  );
}

Control.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  selectProps: PropTypes.object.isRequired
};

function Option(props) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

Option.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isFocused: PropTypes.bool,
  isSelected: PropTypes.bool
};

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

Placeholder.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired
};

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

SingleValue.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired
};

function ValueContainer(props) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}

ValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.object.isRequired
};

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={clsx(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

MultiValue.propTypes = {
  children: PropTypes.node,
  isFocused: PropTypes.bool,
  removeProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired
};

function Menu(props) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}

Menu.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.object,
  selectProps: PropTypes.object
};

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};

export default function IntegrationReactSelect(props) {
  const classes = useStyles(props);
  const theme = useTheme();


  function handleChangeSingle(value) {
    props.onSelectValue(value)
  }

  function handleChangeMulti(value) {
    props.onSelectValue(value)
  }

  const selectStyles = {
    input: base => ({
      ...base,
      color: theme.palette.text.secondary,
      "& input": {
        font: "inherit"
      },
    })
  };

  const handleInputChangeAsync = debounce(e => {
    props.newSearchText(e);
  }, 500)

  const filterOption = ({ label }, text) => {
    const searchText = text ? text.toLowerCase() : '';
    if (props.showDataWithoutComponentTextFilter) return true;
    else return label.toLowerCase().includes(searchText);
  }

  return (
    <div className={classes.root}>
      <NoSsr>
        {!props.isMulti ? (
          <Select
            style={{ color: "grey" }}
            isDisabled={props.disabled}
            classes={classes}
            styles={selectStyles}
            inputId={`react-select-single-${props.id}`}
            placeholder={props.placeholder}
            TextFieldProps={{
              error: !!props.hasError,
              label: props.label,
              InputLabelProps: {
                htmlFor: "react-select-single",
                shrink: true
              },
              placeholder: props.placeholder
            }}
            options={props.data}
            components={components}
            value={props.valueSelected}
            onChange={handleChangeSingle}
            onInputChange={handleInputChangeAsync}
            isLoading={props.loading}
            formatOptionLabel={props.customizedOption}
            isClearable
            filterOption={filterOption}
          />
        ) : (
            <Select
              isDisabled={props.disabled}
              classes={classes}
              styles={selectStyles}
              inputId={`react-select-multiple-${props.id}`}
              placeholder={props.placeholder}
              TextFieldProps={{
                error: !!props.hasError,
                label: props.label,
                InputLabelProps: {
                  htmlFor: "react-select-multiple",
                  shrink: true
                },
                placeholder: props.placeholder
              }}
              options={props.data}
              components={components}
              value={props.valueSelected}
              onChange={handleChangeMulti}
              isLoading={props.loading}
              onInputChange={handleInputChangeAsync}
              formatOptionLabel={props.customizedOption}
              isMulti
              filterOption={filterOption}
            />
          )}
      </NoSsr>
    </div>
  );
}
